import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  CalendarDays,
  Clock3,
  StickyNote,
  AlertTriangle,
} from "lucide-react";

import FormModal from "../../../components/form/FormModal";
import FormInput from "../../../components/form/FormInput";

const toISODate = (val) => {
  if (!val) return "";
  if (val instanceof Date) return val.toISOString().slice(0, 10);

  const s = String(val);

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  if (/^\d{2}-\d{2}-\d{4}$/.test(s)) {
    const [dd, mm, yyyy] = s.split("-");
    return `${yyyy}-${mm}-${dd}`;
  }

  if (s.includes("T")) return s.slice(0, 10);

  return s;
};

const toHHMM = (s) => {
  if (!s) return "";

  const [h, m] = String(s).split(":");

  return `${String(h || "00").padStart(2, "0")}:${String(m || "00").padStart(
    2,
    "0",
  )}`;
};

const toHourOnlyHHMM = (s) => {
  if (!s) return "";

  const [h] = String(s).split(":");

  return `${String(h || "00").padStart(2, "0")}:00`;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const DOW = [
  { label: "Sun", value: 0 },
  { label: "Mon", value: 1 },
  { label: "Tue", value: 2 },
  { label: "Wed", value: 3 },
  { label: "Thu", value: 4 },
  { label: "Fri", value: 5 },
  { label: "Sat", value: 6 },
];

const emptyBlock = {
  mode: "single",
  date: todayISO(),
  startDate: "",
  endDate: "",
  startTime: "06:00",
  endTime: "07:00",
  wholeDay: false,
  days: [],
  note: "",
};

const isWholeDayTimes = (startTime, endTime) =>
  String(startTime) === "00:00" && String(endTime) === "23:59";

const cx = (...arr) => arr.filter(Boolean).join(" ");

const pickBestErrorMessage = (error) => {
  const data = error?.response?.data || {};
  const message =
    data?.message || data?.error || error?.message || "Something went wrong";

  return String(message || "Something went wrong");
};

const BlockAvailabilityForm = ({
  open,
  mode = "create",
  initial = emptyBlock,
  onCancel,
  onSubmit,
  showGroundPicker = false,
  venues = [],
  ensureGroundsForVenue,
  initialVenueId = "",
  initialGroundId = "",
  forceVenueId = "",
  externalError = "",
  externalErrorDetail = "",
  setExternalError,
}) => {
  const [form, setForm] = useState(emptyBlock);
  const [venueId, setVenueId] = useState(initialVenueId || "");
  const [groundId, setGroundId] = useState(initialGroundId || "");
  const [grounds, setGrounds] = useState([]);
  const [loadingGrounds, setLoadingGrounds] = useState(false);
  const [localError, setLocalError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isReadOnlyGroupMeta = !!form.readOnlyGroupMeta;
  const showTimeFields = !form.wholeDay;
  const errorToShow = externalError || localError;

  useEffect(() => {
    const next = { ...emptyBlock, ...(initial || {}) };

    if (
      next.startTime &&
      next.endTime &&
      isWholeDayTimes(next.startTime, next.endTime)
    ) {
      next.wholeDay = true;
    }

    if (next.startDate || next.endDate) {
      next.mode = "range";
    } else {
      next.mode = "single";
    }

    if (!next.note && next.reason) {
      next.note = next.reason;
    }

    setForm(next);
    setLocalError("");

    if (showGroundPicker) {
      const nextVenueId = forceVenueId || initialVenueId || "";
      setVenueId(nextVenueId);
      setGroundId(initialGroundId || "");
    }
  }, [initial, showGroundPicker, initialVenueId, initialGroundId, forceVenueId]);

  useEffect(() => {
    if (!showGroundPicker) return;

    let alive = true;

    const loadGrounds = async () => {
      if (!venueId) {
        setGrounds([]);
        setGroundId("");
        return;
      }

      setLoadingGrounds(true);

      try {
        const list = await ensureGroundsForVenue?.(venueId);
        if (!alive) return;

        const nextGrounds = Array.isArray(list) ? list : [];
        setGrounds(nextGrounds);

        const exists = nextGrounds.some(
          (ground) => String(ground._id || ground.id) === String(groundId),
        );

        if (!exists) {
          setGroundId("");
        }
      } finally {
        if (alive) setLoadingGrounds(false);
      }
    };

    loadGrounds();

    return () => {
      alive = false;
    };
  }, [venueId, groundId, showGroundPicker, ensureGroundsForVenue]);

  const set = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const clearAnyError = () => {
    setLocalError("");
    setExternalError?.("");
  };

  const toggleDow = (dowVal) => {
    clearAnyError();

    setForm((prev) => {
      const nextSet = new Set(Array.isArray(prev.days) ? prev.days : []);

      if (nextSet.has(dowVal)) {
        nextSet.delete(dowVal);
      } else {
        nextSet.add(dowVal);
      }

      return {
        ...prev,
        days: Array.from(nextSet).sort((a, b) => a - b),
      };
    });
  };

  const clearDays = () => {
    clearAnyError();
    set("days", []);
  };

  const setWeekdays = () => {
    clearAnyError();
    set("days", [1, 2, 3, 4, 5]);
  };

  const setAllDays = () => {
    clearAnyError();
    set("days", [0, 1, 2, 3, 4, 5, 6]);
  };

  const validate = () => {
    if (showGroundPicker) {
      if (!venueId) {
        toast.error("Select a venue.");
        return false;
      }

      if (!groundId) {
        toast.error("Select a ground.");
        return false;
      }
    }

    const selectedMode = form.mode || "single";

    if (selectedMode === "single") {
      if (!form.date) {
        toast.error("Pick a date.");
        return false;
      }
    } else {
      const start = form.startDate ? toISODate(form.startDate) : todayISO();
      const end = form.endDate ? toISODate(form.endDate) : "";

      if (end && String(start) > String(end)) {
        toast.error("From date must be before To date.");
        return false;
      }
    }

    if (!form.wholeDay) {
      if (!form.startTime || !form.endTime) {
        toast.error("Pick a time range.");
        return false;
      }
    }

    return true;
  };

  const buildPayload = () => {
    const selectedMode = form.mode || "single";

    const payload = {
      wholeDay: !!form.wholeDay,
      note: (form.note || "").trim(),
    };

    if (selectedMode === "single") {
      payload.date = toISODate(form.date);
    } else {
      payload.startDate = toISODate(form.startDate || todayISO());

      if (form.endDate) {
        payload.endDate = toISODate(form.endDate);
      }

      if (Array.isArray(form.days) && form.days.length) {
        payload.days = form.days;
      }
    }

    if (form.wholeDay) {
      payload.startTime = "00:00";
      payload.endTime = "23:59";
    } else {
      payload.startTime = toHHMM(toHourOnlyHHMM(form.startTime));
      payload.endTime = toHHMM(toHourOnlyHHMM(form.endTime));
    }

    return payload;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (submitting) return;

    setLocalError("");

    if (!validate()) return;

    try {
      setSubmitting(true);

      const payload = buildPayload();
      await onSubmit?.(payload, showGroundPicker ? groundId : null);
    } catch (error) {
      const message = pickBestErrorMessage(error);
      setLocalError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    onCancel?.();
  };

  return (
    <FormModal
      open={open}
      title={mode === "edit" ? "Edit Block" : "Block Slot"}
      width="760px"
      submitting={submitting}
      submitText="Save"
      submittingText="Saving..."
      cancelText="Cancel"
      onClose={handleClose}
      onSubmit={handleSubmit}
    >
      <div className="gavx-form-root">
        <div className="gavx-form-subtitle">
          {mode === "edit"
            ? "Update date/time or switch to whole-day block."
            : "Choose a ground and define date/time, or block the whole day."}
        </div>

        {!!errorToShow && (
          <div className="gavx-error-box">
            <AlertTriangle size={16} className="gavx-error-icon" />

            <div>
              <div className="gavx-error-title">
                {externalError ? "Conflict" : "Could not save"}
              </div>

              <div>{errorToShow}</div>

              {!!externalErrorDetail && (
                <div className="gavx-error-detail">{externalErrorDetail}</div>
              )}
            </div>
          </div>
        )}

        {showGroundPicker && (
          <div className="gavx-section">
            <div className="gavx-section-title">Target</div>

            <div className="gavx-grid">
              <div className="gavx-field">
                <label>Venue</label>

                <select
                  className="gavx-control"
                  value={venueId}
                  onChange={(event) => {
                    clearAnyError();
                    setVenueId(event.target.value);
                  }}
                  disabled={venues.length === 0 || !!forceVenueId}
                >
                  <option value="">
                    {venues.length
                      ? forceVenueId
                        ? "Venue selected"
                        : "Select venue"
                      : "Loading venues..."}
                  </option>

                  {venues.map((venue) => (
                    <option
                      key={venue._id || venue.id}
                      value={venue._id || venue.id}
                    >
                      {venue.name ||
                        venue.title ||
                        venue.slug ||
                        (venue._id || venue.id)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="gavx-field">
                <label>Ground</label>

                <select
                  className="gavx-control"
                  value={groundId}
                  onChange={(event) => {
                    clearAnyError();
                    setGroundId(event.target.value);
                  }}
                  disabled={!venueId || loadingGrounds}
                >
                  <option value="">
                    {!venueId
                      ? "Select venue first"
                      : loadingGrounds
                      ? "Loading grounds..."
                      : "Select ground"}
                  </option>

                  {grounds.map((ground) => (
                    <option
                      key={ground._id || ground.id}
                      value={ground._id || ground.id}
                    >
                      {ground.name || ground.title || (ground._id || ground.id)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        <div className="gavx-section">
          <div className="gavx-section-row">
            <div className="gavx-section-title">
              <CalendarDays size={16} /> Schedule
            </div>

            <div className="gavx-segment">
              <button
                type="button"
                disabled={isReadOnlyGroupMeta}
                className={cx((form.mode || "single") === "single" && "active")}
                onClick={() => {
                  clearAnyError();
                  setForm((prev) => ({
                    ...prev,
                    mode: "single",
                    startDate: "",
                    endDate: "",
                    days: [],
                  }));
                }}
              >
                Single date
              </button>

              <button
                type="button"
                disabled={isReadOnlyGroupMeta}
                className={cx((form.mode || "single") === "range" && "active")}
                onClick={() => {
                  clearAnyError();
                  setForm((prev) => ({
                    ...prev,
                    mode: "range",
                    startDate: prev.startDate || todayISO(),
                    endDate: prev.endDate || "",
                    days: Array.isArray(prev.days) ? prev.days : [],
                  }));
                }}
              >
                Range / Repeat
              </button>
            </div>
          </div>

          {(form.mode || "single") === "single" ? (
            <div className="gavx-grid">
              <div className="gavx-field gavx-span-2">
                <label>Date</label>

                <input
                  className="gavx-control"
                  type="date"
                  value={form.date || ""}
                  disabled={isReadOnlyGroupMeta}
                  onChange={(event) => {
                    clearAnyError();
                    set("date", event.target.value);
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="gavx-grid">
              <div className="gavx-field">
                <label>From (defaults to today)</label>

                <input
                  className="gavx-control"
                  type="date"
                  value={form.startDate || ""}
                  disabled={isReadOnlyGroupMeta}
                  onChange={(event) => {
                    clearAnyError();
                    set("startDate", event.target.value);
                  }}
                />

                <div className="gavx-help">
                  If empty, we will start from today automatically.
                </div>
              </div>

              <div className="gavx-field">
                <label>To (optional)</label>

                <input
                  className="gavx-control"
                  type="date"
                  value={form.endDate || ""}
                  disabled={isReadOnlyGroupMeta}
                  onChange={(event) => {
                    clearAnyError();
                    set("endDate", event.target.value);
                  }}
                />

                <div className="gavx-help">
                  Leave empty for an ongoing recurring block when repeat days are selected.
                </div>
              </div>

              <div className="gavx-field gavx-span-2">
                <label>Repeat on days (optional)</label>

                <div className="gavx-mini-actions">
                  <button
                    type="button"
                    className="gavx-mini-btn"
                    onClick={clearDays}
                    disabled={isReadOnlyGroupMeta}
                  >
                    Clear
                  </button>

                  <button
                    type="button"
                    className="gavx-mini-btn"
                    onClick={setWeekdays}
                    disabled={isReadOnlyGroupMeta}
                  >
                    Weekdays
                  </button>

                  <button
                    type="button"
                    className="gavx-mini-btn"
                    onClick={setAllDays}
                    disabled={isReadOnlyGroupMeta}
                  >
                    All days
                  </button>
                </div>

                <div className="gavx-chip-row">
                  {DOW.map((day) => {
                    const selected = Array.isArray(form.days)
                      ? form.days.includes(day.value)
                      : false;

                    return (
                      <button
                        key={day.value}
                        type="button"
                        className={cx("gavx-chip", selected && "active")}
                        disabled={isReadOnlyGroupMeta}
                        onClick={() => toggleDow(day.value)}
                        title="Blocks only the selected weekdays within the From–To window"
                      >
                        {day.label}
                      </button>
                    );
                  })}
                </div>

                {isReadOnlyGroupMeta && (
                  <div className="gavx-help">
                    For a multi-date grouped block, date range and selected days are
                    read-only here. You can update only time and note.
                  </div>
                )}

                <div className="gavx-help">
                  If you leave days empty, it applies to <b>every day</b> between
                  From–To. If you select <b>Mon</b>, it blocks <b>all Mondays</b>{" "}
                  within the From–To window.
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="gavx-section">
          <div className="gavx-section-row">
            <div className="gavx-section-title">
              <Clock3 size={16} /> Time
            </div>

            <label className="gavx-check">
              <input
                type="checkbox"
                checked={!!form.wholeDay}
                onChange={(event) => {
                  clearAnyError();
                  set("wholeDay", event.target.checked);
                }}
              />

              <span>Block whole day</span>
            </label>
          </div>

          <div className="gavx-grid">
            <div className="gavx-field">
              <label>Start Time</label>

              <div className={cx("gavx-time-wrap", !showTimeFields && "is-disabled")}>
                <FormInput
                  type="time"
                  step="3600"
                  value={form.startTime || "06:00"}
                  onChange={(event) => {
                    clearAnyError();
                    set("startTime", toHourOnlyHHMM(event.target.value));
                  }}
                  disabled={!showTimeFields}
                />
              </div>
            </div>

            <div className="gavx-field">
              <label>End Time</label>

              <div className={cx("gavx-time-wrap", !showTimeFields && "is-disabled")}>
                <FormInput
                  type="time"
                  step="3600"
                  value={form.endTime || "07:00"}
                  onChange={(event) => {
                    clearAnyError();
                    set("endTime", toHourOnlyHHMM(event.target.value));
                  }}
                  disabled={!showTimeFields}
                />
              </div>
            </div>
          </div>

          <div className="gavx-help">Whole day blocks 00:00–23:59 automatically.</div>
        </div>

        <div className="gavx-section">
          <div className="gavx-section-title">
            <StickyNote size={16} /> Note (optional)
          </div>

          <div className="gavx-grid">
            <div className="gavx-field gavx-span-2">
              <label>Reason</label>

              <input
                className="gavx-control"
                type="text"
                placeholder="Maintenance, event, holiday…"
                value={form.note || ""}
                onChange={(event) => {
                  clearAnyError();
                  set("note", event.target.value);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </FormModal>
  );
};

export { emptyBlock, toISODate, toHHMM, todayISO, isWholeDayTimes };
export default BlockAvailabilityForm;